"use client";

import { c as _c } from "react/compiler-runtime";
import { LoanProduct, Page, ProductDetailPage } from "@/sanity/sanity.types";
import ProductArticles from "./product-articles/product-articles";
import ProductCards from "./product-cards/product-cards";
import ProductFooter from "./product-footer/product-footer";
import ProductHero from "./product-hero/product-hero";
import ProductPoints from "./product-points/product-points";
import css from "./product.module.scss";
import FAQ from "@/components/common/faq/faq";
import GridColumn from "@/components/common/grid-column/grid-column";
import GridRow from "@/components/common/grid-row/grid-row";
import { ArticleData } from "@/sanity/interfaces";
import { usePerformance } from "@/contexts/performance-context";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useLayout } from "@/contexts/layout-context";
import HowItWorks from "./how-it-works";
import ComparisonTable from "./comparison-table";
import ProsConsSection from "./pros-cons-section";
import Eligibility from "./eligibility";
import SmartUse from "./smart-use";
import dynamic from "next/dynamic";
const AiCalculatorPopup = dynamic(() => import("@/components/ai-calculator-popup/ai-calculator-popup"), {
  ssr: false
});
interface IProductProps {
  data: LoanProduct;
  detailData: ProductDetailPage;
  latestArticles: ArticleData[];
  beforeQueryTime: number;
  afterQueryTime: number;
  articleName?: string;
  aiCalculatorData?: Page;
}
const Product = props => {
  const $ = _c(48);
  const {
    detailData,
    latestArticles,
    beforeQueryTime,
    afterQueryTime,
    aiCalculatorData
  } = props;
  const {
    benefits,
    faq,
    featureCombo,
    helpCta,
    hero,
    productsGrid,
    comparisonTable,
    prosConsSection,
    eligibility,
    smartUse
  } = detailData;
  const {
    checkTimeElapsed
  } = usePerformance();
  const {
    setAiCalculatorData
  } = useLayout();
  const pathname = usePathname();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["/loan/home-equity-loan", "/loan/home-equity-line-of-credit-heloc", "/loan/cash-out-refinance"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const allowedPaths = t0;
  const showCalculator = allowedPaths.includes(pathname);
  let t1;
  if ($[1] !== afterQueryTime || $[2] !== aiCalculatorData || $[3] !== beforeQueryTime || $[4] !== checkTimeElapsed || $[5] !== setAiCalculatorData) {
    t1 = () => {
      checkTimeElapsed(beforeQueryTime, "Product page before query started");
      checkTimeElapsed(afterQueryTime, "Product page after query completed");
      setAiCalculatorData(aiCalculatorData);
    };
    $[1] = afterQueryTime;
    $[2] = aiCalculatorData;
    $[3] = beforeQueryTime;
    $[4] = checkTimeElapsed;
    $[5] = setAiCalculatorData;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  if ($[8] !== hero) {
    t3 = hero && <ProductHero data={hero} />;
    $[8] = hero;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== benefits) {
    t4 = benefits && <ProductCards data={benefits} />;
    $[10] = benefits;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== featureCombo) {
    t5 = featureCombo && <ProductPoints data={featureCombo} />;
    $[12] = featureCombo;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== productsGrid) {
    t6 = productsGrid && <HowItWorks data={productsGrid} />;
    $[14] = productsGrid;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== smartUse) {
    t7 = smartUse && <SmartUse data={smartUse} />;
    $[16] = smartUse;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== eligibility) {
    t8 = eligibility && <Eligibility data={eligibility} />;
    $[18] = eligibility;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== comparisonTable) {
    t9 = comparisonTable && <ComparisonTable data={comparisonTable} />;
    $[20] = comparisonTable;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== prosConsSection) {
    t10 = prosConsSection && <ProsConsSection data={prosConsSection} />;
    $[22] = prosConsSection;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== faq) {
    t11 = faq && <GridRow><GridColumn><FAQ data={faq} /></GridColumn></GridRow>;
    $[24] = faq;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== latestArticles || $[27] !== props.articleName) {
    t12 = latestArticles && <ProductArticles data={latestArticles} articleName={props?.articleName || "Related Articles"} />;
    $[26] = latestArticles;
    $[27] = props.articleName;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== helpCta) {
    t13 = helpCta && <ProductFooter data={helpCta} />;
    $[29] = helpCta;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t3 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6 || $[39] !== t7 || $[40] !== t8 || $[41] !== t9) {
    t14 = <div className={css.product}>{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}</div>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== showCalculator) {
    t15 = showCalculator && <GridRow><GridColumn lg={12}><AiCalculatorPopup /></GridColumn></GridRow>;
    $[43] = showCalculator;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t14 || $[46] !== t15) {
    t16 = <>{t14}{t15}</>;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
};
export default Product;